import Head from 'next/head';
import { useRouter } from 'next/router';

import { SeoData } from '@api/seo/types';

import { useRequestOrigin } from '@context/requestOrigin';

import { getSiteOrigin } from '@utils/config';

import {
    DEFAULT_OG_IMAGE_HEIGHT,
    DEFAULT_OG_IMAGE_TYPE,
    DEFAULT_OG_IMAGE_WIDTH,
    isDefaultOgImage,
    resolveOgImageUrl,
} from '@scripts/seo/ogImage';
import { toAbsoluteUrl } from '@scripts/seo/toAbsoluteUrl';

import { getOgType } from './getOgType';
import { isCmsNoindex } from './isCmsNoindex';

const buildPaginationHref = (baseUrl: string | undefined, page: number) => {
    if (!baseUrl) {
        return undefined;
    }

    if (page <= 1) {
        return baseUrl;
    }

    return `${baseUrl}?page=${page}`;
};

/** CMS/router often give `/path`; crawlers require an absolute canonical. */
const toAbsoluteCanonical = (canonical?: string): string | undefined => {
    if (!canonical) {
        return undefined;
    }

    const origin = getSiteOrigin();

    if (!origin) {
        return canonical.startsWith('http://') || canonical.startsWith('https://') ? canonical : undefined;
    }

    return toAbsoluteUrl(origin, canonical);
};

export const SeoHead = ({
    title,
    canonical,
    description,
    image,
    activePage,
    totalPages,
    nameDigi,
    contentDigi,
    is_indexing,
}: SeoData) => {
    const { pathname } = useRouter();
    const requestOrigin = useRequestOrigin();
    const ogType = getOgType(pathname);
    const shouldNoindex = isCmsNoindex(is_indexing);
    const absoluteCanonical = toAbsoluteCanonical(canonical);
    const ogImage = resolveOgImageUrl(image, requestOrigin);
    const useDefaultOgImage = isDefaultOgImage(image);

    const canonicalWithoutPage = () => {
        if (absoluteCanonical?.includes('page')) {
            return absoluteCanonical.split('?')?.[0];
        }

        return absoluteCanonical;
    };

    const page = activePage ?? 0;
    const pages = totalPages ?? 0;
    const baseUrl = canonicalWithoutPage();
    const hasPagination = page > 0 && pages > 0;

    return (
        <Head>
            {title && <title>{title}</title>}
            {absoluteCanonical && <link rel="canonical" href={absoluteCanonical} />}
            {description && <meta name="description" content={description} />}
            {shouldNoindex && <meta name="robots" content="noindex,follow" />}
            {title && <meta property="og:title" content={title} />}
            {description && <meta property="og:description" content={description} />}
            <meta property="og:type" content={ogType} />
            <meta property="og:site_name" content="Layta" />
            <meta property="og:locale" content="ru_RU" />
            {absoluteCanonical && <meta property="og:url" content={absoluteCanonical} />}
            {ogImage && <meta property="og:image" content={ogImage} />}
            {ogImage && useDefaultOgImage && (
                <>
                    <meta property="og:image:type" content={DEFAULT_OG_IMAGE_TYPE} />
                    <meta property="og:image:width" content={String(DEFAULT_OG_IMAGE_WIDTH)} />
                    <meta property="og:image:height" content={String(DEFAULT_OG_IMAGE_HEIGHT)} />
                    <meta property="og:image:alt" content="Layta" />
                </>
            )}
            {ogImage && <meta name="twitter:card" content="summary_large_image" />}
            {title && <meta name="twitter:title" content={title} />}
            {description && <meta name="twitter:description" content={description} />}
            {ogImage && <meta name="twitter:image" content={ogImage} />}
            {hasPagination && page === 1 && page < pages && (
                <link rel="next" href={buildPaginationHref(baseUrl, page + 1)} />
            )}
            {hasPagination && page > 1 && page < pages && (
                <>
                    <link rel="next" href={buildPaginationHref(baseUrl, page + 1)} />
                    <link rel="prev" href={buildPaginationHref(baseUrl, page - 1)} />
                </>
            )}
            {hasPagination && page > 1 && page === pages && (
                <link rel="prev" href={buildPaginationHref(baseUrl, page - 1)} />
            )}
            {nameDigi && contentDigi && <meta name={nameDigi} content={contentDigi} />}
        </Head>
    );
};
